import { useToast } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { COUNTRY_OPTIONS } from "@equidefi/constants/countries";
import { SmartFormProvider } from "@equidefi/portals/components/smart-form";
import { postalCodeZodRefinement } from "@equidefi/portals/helpers/postalCode";
import { usePostalCodeExcluded } from "@equidefi/portals/hooks/usePostalCode";
import type { DTO } from "@equidefi/shared/types/dto";

// @ts-ignore
import { MESSAGE } from "@equidefi/constants/forms";
import { SavedProfileFormContainer } from "./SavedProfileFormContainer";

const schema = z
  .object({
    address_street_1: z.string().trim().min(1, MESSAGE.REQUIRED("Street Address")),
    address_street_2: z.string().nullable(),
    address_city: z.string().trim().min(1, MESSAGE.REQUIRED("City")),
    address_state: z.string().trim().min(1, MESSAGE.REQUIRED("State")),
    address_postal_code: z.string().trim().optional().nullable(),
    address_country_code: z.string().trim().min(1, MESSAGE.REQUIRED("Country")),
  })
  .superRefine(
    postalCodeZodRefinement({
      countryField: "address_country_code",
      postalField: "address_postal_code",
      message: MESSAGE.REQUIRED("Postal Code"),
    }),
  );

type HomeAddressFormValues = z.infer<typeof schema>;

type HomeAddressFormNormalized = {
  address_street_1: string;
  address_street_2: string | null;
  address_city: string;
  address_state: string;
  address_postal_code: string;
  address_country_code: string;
};

type HomeAddressFormProps = {
  profile: DTO.SavedProfile;
  onSave: (values: HomeAddressFormNormalized) => Promise<void>;
};

const getInitialValues = (profile: DTO.SavedProfile): HomeAddressFormValues => ({
  address_street_1: profile.address_street_1 ?? "",
  address_street_2: profile.address_street_2 ?? null,
  address_city: profile.address_city ?? "",
  address_state: profile.address_state ?? "",
  address_postal_code: profile.address_postal_code ?? "",
  address_country_code: profile.address_country_code ?? "",
});

const normalizeValues = (values: HomeAddressFormValues): HomeAddressFormNormalized => ({
  address_street_1: values.address_street_1.trim(),
  address_street_2: values.address_street_2?.trim() || null,
  address_city: values.address_city.trim(),
  address_state: values.address_state.trim(),
  address_postal_code: values.address_postal_code?.trim() || "",
  address_country_code: values.address_country_code.trim(),
});

export function HomeAddressForm({ profile, onSave }: HomeAddressFormProps) {
  const toast = useToast();

  const form = useForm<HomeAddressFormValues>({
    defaultValues: getInitialValues(profile),
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  const hidePostalCode = usePostalCodeExcluded(
    form.watch("address_country_code"),
    form.watch("address_postal_code"),
    () => form.setValue("address_postal_code", "", { shouldValidate: true }),
  );

  const onSubmit = useCallback(
    async (values: HomeAddressFormValues) => {
      try {
        await onSave(normalizeValues(values));
      } catch (error) {
        console.error(error);

        toast({
          status: "error",
          description: "There was an error trying to save your home address.",
        });
      }
    },
    [onSave, toast],
  );

  return (
    <SmartFormProvider form={form}>
      {({ Input, Select }) => (
        <SavedProfileFormContainer
          profile={profile}
          title="Home Address"
          subtitle="Enter your legal address, as shown on your government ID."
          onSave={form.handleSubmit(onSubmit)}
          isLoading={form.formState.isSubmitting}
          isDisabled={!form.formState.isValid}>
          <Input name="address_street_1" label="Street Address" isRequired />
          <Input name="address_street_2" label="Street Address 2 (Unit #)" />
          <Input name="address_city" label="City" isRequired />
          <Input name="address_state" label="State" isRequired />
          {!hidePostalCode && <Input name="address_postal_code" label="Postal Code" isRequired />}
          <Select name="address_country_code" label="Country" options={COUNTRY_OPTIONS} isRequired />
        </SavedProfileFormContainer>
      )}
    </SmartFormProvider>
  );
}
